import React, { useEffect } from 'react';
import ApiValidationErrorDisplay from '~/components/ApiValidationErrorDisplay';
import { clearApiValidationErrors } from '~/stores/redux/common/apiValidationErrorSlice';
import { schema } from './equipment.yup';
import { Col } from 'react-bootstrap';
import { FileUploadedWithResetCount } from '~/components/FileUploaded';
import DisplayFormikState from '~/components/DisplayFormikState';
import SubmitResetButtons from '~/components/FormControl/SubmitResetButtons';
import classNames from 'classnames/bind';
const cx = classNames.bind();
import { FormText, FormSelect, TextArea, Selectize } from '~/components/FormControl';
import { Formik } from 'formik';
import { Card, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { checkDuplicateName } from '~/utils/validators.yup';
import { API_PREFIX } from './equipment.api';
import { fetchCardMetaDataApi } from '~/pages/CardSaga/cardMetaDataSlice';
import { useNavigate } from 'react-router-dom';
import DurabilityForm from './DurabilityForm/DurabilityForm';

EquipmentForm.defaultProps = {
    pictureAttributeNames: null,
};
EquipmentForm.propTypes = {
    initialValues: PropTypes.object.isRequired,
    setResetCount: PropTypes.func.isRequired,
    pictureAttributeNames: PropTypes.array,
    resetCount: PropTypes.number.isRequired,
    formSubmit: PropTypes.func.isRequired,
};

function EquipmentForm({ initialValues, setResetCount, pictureAttributeNames, resetCount, formSubmit }) {
    const { isLoading } = useSelector((state) => state.loading);
    const { apiValidationErrors } = useSelector((state) => state.apiValidationError);
    const { equipmentCategories, equipmentDurabilities } = useSelector((state) => state.heroSaga.equipment);
    const { cardMetaData } = useSelector((state) => state.cardSaga.cardMetaData);
    const { actions, powers, upgrades, characterClasses } = cardMetaData;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (isEmpty(cardMetaData)) {
            dispatch(fetchCardMetaDataApi(navigate));
        }
    }, []);

    return (
        <>
            {/* MUST put `isEmpty(heroMetaData)` in condition, else Bootstrap `Select` will not display value to UI (even though it's already stored value in it)  */}
            {!isEmpty(initialValues) && !isEmpty(cardMetaData) ? (
                <Card>
                    <Card.Body>
                        <Formik
                            initialValues={initialValues}
                            validateOnChange={false}
                            validationSchema={initialValues.id === null ? schema : schema(true)}
                            onSubmit={formSubmit}
                        >
                            {(props) => {
                                // eslint-disable-next-line react/prop-types
                                const { handleSubmit, isSubmitting, resetForm, values } = props;
                                const asyncApiCall = checkDuplicateName(initialValues.name, API_PREFIX); // must put inside formik's callback, so that this `checkDuplicateName` will always return new closure every time formik re-renders, which is necessary as I use <FastField> with `validate` prop, which under the hood compares its current props with last props and only re-render if props actually change. But if I put this snippet outside formik's callback, then `asyncApiCall` of the next re-render is just of the original one from the scope above (this is one of closure features) so it will not re-render even though param passed to `asyncApiCall` (`initialValues.name`) has actually changed.

                                return (
                                    <>
                                        <SubmitResetButtons
                                            isSubmitting={isSubmitting}
                                            handleSubmit={handleSubmit}
                                            setResetCount={setResetCount}
                                            resetForm={resetForm}
                                        />
                                        <Form.Row>
                                            <FormText
                                                sm="3"
                                                name="name"
                                                asyncApiCall={asyncApiCall}
                                                className="d-flex flex-column justify-content-end"
                                            />
                                            {pictureAttributeNames.map((imgName, i) => (
                                                <Col
                                                    key={i}
                                                    sm={4}
                                                    className={i === 0 ? 'offset-sm-1' : ''}
                                                >
                                                    <FileUploadedWithResetCount
                                                        pictureName={imgName}
                                                        pictureVal={values[imgName]}
                                                        resetCount={resetCount}
                                                        id={imgName}
                                                    />
                                                </Col>
                                            ))}
                                        </Form.Row>
                                        <Form.Row className="mt-3">
                                            <TextArea
                                                name={'description'}
                                                sm={3}
                                                rows={4}
                                            />
                                            <Selectize
                                                name="categories"
                                                options={equipmentCategories}
                                                sm={2}
                                                className={'offset-sm-1'}
                                            />
                                            <FormSelect
                                                name="characterClassId"
                                                options={characterClasses}
                                                sm={2}
                                                label="Character Class"
                                            />
                                            <FormSelect
                                                name="upgradeId"
                                                options={[...powers, ...upgrades]}
                                                sm={2}
                                                label="Upgrade"
                                            />
                                            <FormSelect
                                                name="actionId"
                                                options={[...actions, ...powers]}
                                                sm={2}
                                                label="Action"
                                            />
                                        </Form.Row>

                                        <Form.Row className="mt-3">
                                            <DurabilityForm
                                                equipmentDurabilities={equipmentDurabilities}
                                                // eslint-disable-next-line react/prop-types
                                                durabilities={[...values.durabilities]}
                                            />
                                        </Form.Row>

                                        <SubmitResetButtons
                                            isSubmitting={isSubmitting}
                                            handleSubmit={handleSubmit}
                                            setResetCount={setResetCount}
                                            resetForm={resetForm}
                                        />
                                        <ApiValidationErrorDisplay
                                            errors={apiValidationErrors}
                                            clearError={clearApiValidationErrors}
                                        />
                                        <DisplayFormikState props={props} />
                                    </>
                                );
                            }}
                        </Formik>
                    </Card.Body>
                </Card>
            ) : (
                <Card style={{ position: 'relative' }}>
                    <Card.Body className={cx('row', { whirl: isLoading })}></Card.Body>
                </Card>
            )}
        </>
    );
}

export default EquipmentForm;
